import type { UniverseId } from '@/domain/universes/universe.interface';
import { I18nService } from '@/utils/i18n/i18n.service';
import { type ReferenceListItem, ReferenceListName } from '@/utils/reference-lists/reference-lists.interface';
import * as ReferenceListsStoreActions from '@/utils/reference-lists/store/reference-lists.actions';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import type { Action } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import type { UnitList } from '../../interfaces/CombinedList.interface';
import { AdvizeoCoreUnitsProvider } from '../../providers/unit/advizeo-core-units.provider';
import { SetLegacyCombinedLists } from './combined-list.actions';

@Injectable({
    providedIn: 'root'
})
export class CombinedListStoreEffects {
    private _action$ = inject(Actions);
    private _advizeoCoreUnitsProvider = inject(AdvizeoCoreUnitsProvider);
    private _i18n = inject(I18nService);

    setLegacyCombinedListsEffect$: Observable<Action> = createEffect(() =>
        this._action$.pipe(
            ofType<ReferenceListsStoreActions.FetchReferenceListsSucceeded>(
                ReferenceListsStoreActions.ActionType.FETCH_REFERENCE_LISTS_SUCCEEDED
            ),
            map(action => action.payload),
            switchMap(lists => {
                return this._i18n.currentLang$.pipe(
                    take(1),
                    switchMap(lang => this._advizeoCoreUnitsProvider.getAllUnit$(lang)),
                    map(unitList => [lists, unitList] as [Partial<Record<ReferenceListName, ReferenceListItem[]>>, UnitList[]])
                );
            }),
            map(([lists, unitList]) => {
                const unitsLegacy = unitList.map(unitListItem => {
                    const unitLabelReference = lists[ReferenceListName.UNIT_LABEL]?.find(reference => reference.code === unitListItem.code);
                    const unitReference = lists[ReferenceListName.UNIT]?.find(reference => reference.code === unitListItem.code);
                    return {
                        ...unitListItem,
                        label: unitLabelReference ? unitLabelReference.label : '',
                        suffix: unitReference ? unitReference.label : ''
                    };
                });

                const counterUsages = lists[ReferenceListName.USAGE]?.map(usage => {
                    const splittedCode = usage.code.split('--');
                    return {
                        ...usage,
                        code: splittedCode[1],
                        type: splittedCode[0] as UniverseId
                    };
                });

                return new SetLegacyCombinedLists({
                    combinedList: {
                        counterUsages: counterUsages ?? [],
                        unitsLegacy
                    }
                });
            })
        )
    );
}
